import React, { useMemo } from "react";
import {
  Box,
  Chip,
  Drawer,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import type { CapabilityMapDataset, CapabilityMapSelection, NodeKind } from "./types";

interface Props {
  open: boolean;
  selection: CapabilityMapSelection;
  dataset: CapabilityMapDataset;
  onClose: () => void;
  onNodeClick: (nodeId: string, kind: NodeKind) => void;
}

const KIND_LABELS: Record<string, string> = {
  capability_cluster: "Capability Cluster",
  sub_capability: "Sub-Capability",
  use_case: "Use Case",
  tender: "Tender / Program",
  system_type: "System Type",
  system_instance: "System Instance",
  system_capability: "System Capability",
  component_capability: "Component Capability",
  component_type: "Component Type",
  component_instance: "Component Instance",
  company: "Company",
  icp_cluster: "Buyer / ICP",
};

const KIND_COLORS: Record<string, string> = {
  capability_cluster: "#1565C0",
  sub_capability: "#6A1B9A",
  use_case: "#2E7D32",
  tender: "#5D4037",
  system_type: "#1565C0",
  system_instance: "#4527A0",
  system_capability: "#00695C",
  component_capability: "#558B2F",
  component_type: "#2E7D32",
  component_instance: "#BF360C",
  company: "#424242",
  icp_cluster: "#E65100",
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  formal_tender: "Formal Tender",
  press_release: "Press Release",
  program_announcement: "Programme Announcement",
  patent: "Patent",
};

const SOURCE_TYPE_COLORS: Record<string, string> = {
  formal_tender: "#1565C0",
  press_release: "#795548",
  program_announcement: "#4527A0",
  patent: "#37474F",
};

const STATUS_COLORS: Record<string, string> = {
  crowded: "#b71c1c",
  moderate: "#f57f17",
  open: "#1b5e20",
};

const CapabilityMapDetailPanel: React.FC<Props> = ({
  open,
  selection,
  dataset,
  onClose,
  onNodeClick,
}) => {
  const content = useMemo(() => {
    if (!selection.nodeId || !selection.nodeKind) return null;
    const { nodeId, nodeKind } = selection;

    // ── Legacy node kinds ────────────────────────────────────────────────────
    if (nodeKind === "capability_cluster") {
      const cluster = dataset.clusters.find((c) => c.id === nodeId);
      if (!cluster) return null;
      const companies = dataset.companies.filter((c) =>
        cluster.subCapabilities.some((sc) => c.capabilitiesCovered.includes(sc.id)),
      );
      return (
        <>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>{cluster.description}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Sub-Capabilities ({cluster.subCapabilities.length})</Typography>
          <List dense disablePadding sx={{ mb: 2 }}>
            {cluster.subCapabilities.map((sc) => (
              <ListItemButton key={sc.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(sc.id, "sub_capability")}>
                <ListItemText primary={sc.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#ce93d8" } }} />
              </ListItemButton>
            ))}
          </List>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Companies ({companies.length})</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {companies.map((c) => (
              <Chip key={c.id} label={c.label} size="small" sx={{ fontSize: "0.65rem", cursor: "pointer" }} onClick={() => onNodeClick(c.id, "company")} />
            ))}
          </Box>
        </>
      );
    }

    if (nodeKind === "sub_capability") {
      const sc = dataset.clusters.flatMap((c) => c.subCapabilities).find((s) => s.id === nodeId);
      if (!sc) return null;
      const companies = dataset.companies.filter((c) => c.capabilitiesCovered.includes(nodeId));
      return (
        <>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>{sc.description}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "warning.main", display: "block", mb: 0.5 }}>Why It Matters</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>{sc.whyItMatters}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Typical Buyers</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
            {sc.typicalBuyers.map((b) => (<Chip key={b} label={b} size="small" variant="outlined" sx={{ fontSize: "0.65rem" }} />))}
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Use Cases ({sc.useCases.length})</Typography>
          <List dense disablePadding sx={{ mb: 1.5 }}>
            {sc.useCases.map((uc) => (
              <ListItemButton key={uc.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(uc.id, "use_case")}>
                <ListItemText primary={uc.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#a5d6a7" } }} />
                <Chip label={uc.marketStatus} size="small" sx={{ height: 16, fontSize: "0.58rem", bgcolor: STATUS_COLORS[uc.marketStatus], color: "#fff", ml: 1 }} />
              </ListItemButton>
            ))}
          </List>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Companies ({companies.length})</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {companies.map((c) => (<Chip key={c.id} label={c.label} size="small" sx={{ fontSize: "0.65rem", cursor: "pointer" }} onClick={() => onNodeClick(c.id, "company")} />))}
          </Box>
        </>
      );
    }

    if (nodeKind === "use_case") {
      const uc = dataset.clusters.flatMap((c) => c.subCapabilities).flatMap((sc) => sc.useCases).find((u) => u.id === nodeId);
      if (!uc) return null;
      const companies = dataset.companies.filter((c) => uc.companyIds.includes(c.id));
      return (
        <>
          <Chip label={uc.marketStatus} size="small" sx={{ mb: 1.5, bgcolor: STATUS_COLORS[uc.marketStatus], color: "#fff", fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase" }} />
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>{uc.description}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Tags</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
            {uc.useCaseTags.map((t) => (<Chip key={t} label={t} size="small" variant="outlined" sx={{ fontSize: "0.65rem" }} />))}
            {uc.domains.map((d) => (<Chip key={d} label={d} size="small" variant="outlined" sx={{ fontSize: "0.65rem", borderColor: "primary.main", color: "primary.main" }} />))}
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Key Companies</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {companies.map((c) => (<Chip key={c.id} label={c.label} size="small" sx={{ fontSize: "0.65rem", cursor: "pointer" }} onClick={() => onNodeClick(c.id, "company")} />))}
          </Box>
        </>
      );
    }

    if (nodeKind === "company") {
      const company = dataset.companies.find((c) => c.id === nodeId);
      if (!company) return null;
      const coveredSubCaps = dataset.clusters.flatMap((cl) => cl.subCapabilities).filter((sc) => company.capabilitiesCovered.includes(sc.id));
      const componentInstances = dataset.componentInstances.filter((ci) => company.componentInstanceIds.includes(ci.id));
      const systemInstances = dataset.systemInstances.filter((si) => company.systemInstanceIds.includes(si.id));
      return (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
            {company.companyTypes.map((t) => (<Chip key={t} label={t} size="small" variant="outlined" sx={{ fontSize: "0.65rem" }} />))}
            {company.domains.map((d) => (<Chip key={d} label={d} size="small" variant="outlined" sx={{ fontSize: "0.65rem", borderColor: "primary.main", color: "primary.main" }} />))}
          </Box>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>{company.description}</Typography>
          {componentInstances.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Products / Components ({componentInstances.length})</Typography>
              <List dense disablePadding sx={{ mb: 1.5 }}>
                {componentInstances.map((ci) => (
                  <ListItemButton key={ci.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(ci.id, "component_instance")}>
                    <ListItemText primary={ci.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#FFAB91" } }} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
          {systemInstances.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Systems ({systemInstances.length})</Typography>
              <List dense disablePadding sx={{ mb: 1.5 }}>
                {systemInstances.map((si) => (
                  <ListItemButton key={si.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(si.id, "system_instance")}>
                    <ListItemText primary={si.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#CE93D8" } }} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Capability Coverage ({coveredSubCaps.length})</Typography>
          <List dense disablePadding sx={{ mb: 1.5 }}>
            {coveredSubCaps.map((sc) => (
              <ListItemButton key={sc.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(sc.id, "sub_capability")}>
                <ListItemText primary={sc.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#ce93d8" } }} />
              </ListItemButton>
            ))}
          </List>
          {company.icp.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Target Buyers (ICP)</Typography>
              <List dense disablePadding>
                {company.icp.map((icp) => (
                  <ListItemButton key={icp.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(icp.id, "icp_cluster")}>
                    <ListItemText primary={icp.label} secondary={icp.description} primaryTypographyProps={{ variant: "caption", sx: { color: "#ffcc80" } }} secondaryTypographyProps={{ variant: "caption", sx: { fontSize: "0.65rem" } }} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </>
      );
    }

    if (nodeKind === "icp_cluster") {
      const icp = dataset.companies.flatMap((c) => c.icp).find((i) => i.id === nodeId);
      if (!icp) return null;
      const relevantCompanies = dataset.companies.filter((c) => c.icp.some((i) => i.id === nodeId));
      return (
        <>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>{icp.description}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Served By ({relevantCompanies.length} companies)</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {relevantCompanies.map((c) => (<Chip key={c.id} label={c.label} size="small" sx={{ fontSize: "0.65rem", cursor: "pointer" }} onClick={() => onNodeClick(c.id, "company")} />))}
          </Box>
        </>
      );
    }

    // ── New node kinds ────────────────────────────────────────────────────────
    if (nodeKind === "tender") {
      const tender = dataset.tenders.find((t) => t.id === nodeId);
      if (!tender) return null;
      const systemInstances = dataset.systemInstances.filter((si) => tender.systemInstanceIds.includes(si.id));
      const systemTypes = dataset.systemTypes.filter((st) => tender.systemTypeIds.includes(st.id));
      return (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
            <Chip label={SOURCE_TYPE_LABELS[tender.sourceType] ?? tender.sourceType} size="small" sx={{ bgcolor: SOURCE_TYPE_COLORS[tender.sourceType], color: "#fff", fontSize: "0.65rem" }} />
            {tender.domains.map((d) => (<Chip key={d} label={d} size="small" variant="outlined" sx={{ fontSize: "0.65rem", borderColor: "primary.main", color: "primary.main" }} />))}
          </Box>
          {/* Specificity bar */}
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.25 }}>
            Specificity: {Math.round(tender.specificity * 100)}%
          </Typography>
          <LinearProgress variant="determinate" value={tender.specificity * 100} sx={{ mb: 1.5, height: 6, borderRadius: 3, bgcolor: "action.hover", "& .MuiLinearProgress-bar": { bgcolor: tender.specificity >= 0.7 ? "success.main" : tender.specificity >= 0.4 ? "warning.main" : "error.main" } }} />
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>{tender.description}</Typography>
          {tender.contractingAuthority && (
            <><Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.25 }}>Contracting Authority</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>{tender.contractingAuthority}</Typography></>
          )}
          {tender.estimatedValue && (
            <><Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.25 }}>Estimated Value</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>{tender.estimatedValue}</Typography></>
          )}
          {tender.date && (
            <><Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.25 }}>Date</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>{tender.date}</Typography></>
          )}
          <Divider sx={{ my: 1 }} />
          {systemTypes.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>System Types Required</Typography>
              <List dense disablePadding sx={{ mb: 1 }}>
                {systemTypes.map((st) => (
                  <ListItemButton key={st.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(st.id, "system_type")}>
                    <ListItemText primary={st.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#90CAF9" } }} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
          {systemInstances.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>System Instances</Typography>
              <List dense disablePadding>
                {systemInstances.map((si) => (
                  <ListItemButton key={si.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(si.id, "system_instance")}>
                    <ListItemText primary={si.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#CE93D8" } }} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </>
      );
    }

    if (nodeKind === "system_type") {
      const st = dataset.systemTypes.find((s) => s.id === nodeId);
      if (!st) return null;
      const systemCaps = dataset.systemCapabilities.filter((sc) => st.systemCapabilityIds.includes(sc.id));
      const componentTypes = dataset.componentTypes.filter((ct) => st.componentTypeIds.includes(ct.id));
      const instances = dataset.systemInstances.filter((si) => si.systemTypeIds.includes(st.id));
      const tenders = dataset.tenders.filter((t) => t.systemTypeIds.includes(st.id));
      return (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
            {st.domains.map((d) => (<Chip key={d} label={d} size="small" variant="outlined" sx={{ fontSize: "0.65rem", borderColor: "primary.main", color: "primary.main" }} />))}
          </Box>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>{st.description}</Typography>
          {tenders.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Tenders ({tenders.length})</Typography>
              <List dense disablePadding sx={{ mb: 1.5 }}>
                {tenders.map((t) => (
                  <ListItemButton key={t.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(t.id, "tender")}>
                    <ListItemText primary={t.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#FFCC80" } }} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>System Capabilities Required ({systemCaps.length})</Typography>
          <List dense disablePadding sx={{ mb: 1.5 }}>
            {systemCaps.map((sc) => (
              <ListItemButton key={sc.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(sc.id, "system_capability")}>
                <ListItemText primary={sc.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#80CBC4" } }} />
              </ListItemButton>
            ))}
          </List>
          {componentTypes.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Key Component Types ({componentTypes.length})</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
                {componentTypes.map((ct) => (<Chip key={ct.id} label={ct.label} size="small" sx={{ fontSize: "0.65rem", cursor: "pointer" }} onClick={() => onNodeClick(ct.id, "component_type")} />))}
              </Box>
            </>
          )}
          {instances.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Known Instances ({instances.length})</Typography>
              <List dense disablePadding>
                {instances.map((si) => (
                  <ListItemButton key={si.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(si.id, "system_instance")}>
                    <ListItemText primary={si.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#CE93D8" } }} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </>
      );
    }

    if (nodeKind === "system_instance") {
      const si = dataset.systemInstances.find((s) => s.id === nodeId);
      if (!si) return null;
      const systemTypes = dataset.systemTypes.filter((st) => si.systemTypeIds.includes(st.id));
      const systemCaps = dataset.systemCapabilities.filter((sc) => si.systemCapabilityIds.includes(sc.id));
      const componentInstances = dataset.componentInstances.filter((ci) => si.componentInstanceIds.includes(ci.id));
      const company = si.companyId ? dataset.companies.find((c) => c.id === si.companyId) : undefined;
      const tender = si.tenderId ? dataset.tenders.find((t) => t.id === si.tenderId) : undefined;
      return (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
            {tender && <Chip label="Demand-side" size="small" sx={{ bgcolor: "#5D4037", color: "#fff", fontSize: "0.65rem" }} />}
            {company && <Chip label="Supply-side" size="small" sx={{ bgcolor: "#1565C0", color: "#fff", fontSize: "0.65rem" }} />}
          </Box>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>{si.description}</Typography>
          {company && (
            <><Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.25 }}>Manufacturer</Typography>
            <Chip label={company.label} size="small" sx={{ fontSize: "0.65rem", mb: 1.5, cursor: "pointer" }} onClick={() => onNodeClick(company.id, "company")} /></>
          )}
          {tender && (
            <><Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.25 }}>From Tender</Typography>
            <Chip label={tender.label} size="small" sx={{ fontSize: "0.65rem", mb: 1.5, cursor: "pointer", bgcolor: "#5D4037", color: "#fff" }} onClick={() => onNodeClick(tender.id, "tender")} /></>
          )}
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Is a: {systemTypes.map((st) => st.label).join(", ")}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5, mt: 1 }}>System Capabilities ({systemCaps.length})</Typography>
          <List dense disablePadding sx={{ mb: 1.5 }}>
            {systemCaps.map((sc) => (
              <ListItemButton key={sc.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(sc.id, "system_capability")}>
                <ListItemText primary={sc.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#80CBC4" } }} />
              </ListItemButton>
            ))}
          </List>
          {componentInstances.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Component Instances ({componentInstances.length})</Typography>
              <List dense disablePadding>
                {componentInstances.map((ci) => (
                  <ListItemButton key={ci.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(ci.id, "component_instance")}>
                    <ListItemText primary={ci.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#FFAB91" } }} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </>
      );
    }

    if (nodeKind === "system_capability") {
      const sc = dataset.systemCapabilities.find((s) => s.id === nodeId);
      if (!sc) return null;
      const enablerCaps = dataset.componentCapabilities.filter((cc) => sc.enabledByComponentCapIds.includes(cc.id));
      const enablerTypes = dataset.componentTypes.filter((ct) => ct.componentCapabilityIds.some((ccId) => sc.enabledByComponentCapIds.includes(ccId)));
      const demandTenders = dataset.tenders.filter((t) => {
        const stIds = t.systemTypeIds;
        return stIds.some((stId) => dataset.systemTypes.find((st) => st.id === stId)?.systemCapabilityIds.includes(sc.id));
      });
      return (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>Demand:</Typography>
            <Chip
              label={`${sc.demandCount} tender${sc.demandCount !== 1 ? "s" : ""}`}
              size="small"
              sx={{ bgcolor: sc.demandCount >= 3 ? "#b71c1c" : sc.demandCount >= 1 ? "#f57f17" : "#37474F", color: "#fff", fontWeight: 700, fontSize: "0.65rem" }}
            />
            <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.65rem" }}>{sc.clusterLabel}</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>{sc.description}</Typography>
          {demandTenders.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Tenders Requiring This ({demandTenders.length})</Typography>
              <List dense disablePadding sx={{ mb: 1.5 }}>
                {demandTenders.map((t) => (
                  <ListItemButton key={t.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(t.id, "tender")}>
                    <ListItemText primary={t.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#FFCC80" } }} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Enabling Component Capabilities ({enablerCaps.length})</Typography>
          <List dense disablePadding sx={{ mb: 1.5 }}>
            {enablerCaps.map((cc) => (
              <ListItemButton key={cc.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(cc.id, "component_capability")}>
                <ListItemText primary={cc.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#CCFF90" } }} />
              </ListItemButton>
            ))}
          </List>
          {enablerTypes.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Enabling Component Types ({enablerTypes.length})</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {enablerTypes.map((ct) => (<Chip key={ct.id} label={ct.label} size="small" sx={{ fontSize: "0.65rem", cursor: "pointer" }} onClick={() => onNodeClick(ct.id, "component_type")} />))}
              </Box>
            </>
          )}
        </>
      );
    }

    if (nodeKind === "component_capability") {
      const cc = dataset.componentCapabilities.find((c) => c.id === nodeId);
      if (!cc) return null;
      const enabledScs = dataset.systemCapabilities.filter((sc) => cc.enablesSystemCapIds.includes(sc.id));
      const providingInstances = dataset.componentInstances.filter((ci) => ci.componentCapabilityIds.includes(cc.id));
      return (
        <>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>{cc.description}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Enables System Capabilities ({enabledScs.length})</Typography>
          <List dense disablePadding sx={{ mb: 1.5 }}>
            {enabledScs.map((sc) => (
              <ListItemButton key={sc.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(sc.id, "system_capability")}>
                <ListItemText primary={sc.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#80CBC4" } }} />
              </ListItemButton>
            ))}
          </List>
          {providingInstances.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Provided By ({providingInstances.length} products)</Typography>
              <List dense disablePadding>
                {providingInstances.map((ci) => (
                  <ListItemButton key={ci.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(ci.id, "component_instance")}>
                    <ListItemText primary={ci.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#FFAB91" } }} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </>
      );
    }

    if (nodeKind === "component_type") {
      const ct = dataset.componentTypes.find((c) => c.id === nodeId);
      if (!ct) return null;
      const compCaps = dataset.componentCapabilities.filter((cc) => ct.componentCapabilityIds.includes(cc.id));
      const enabledScs = dataset.systemCapabilities.filter((sc) => compCaps.some((cc) => cc.enablesSystemCapIds.includes(sc.id)));
      const instances = dataset.componentInstances.filter((ci) => ci.componentTypeId === ct.id);
      const companies = dataset.companies.filter((co) => instances.some((ci) => ci.companyId === co.id));
      return (
        <>
          <Chip label={ct.levelHint} size="small" sx={{ mb: 1.5, bgcolor: "#37474F", color: "#fff", fontSize: "0.65rem" }} />
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>{ct.description}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Component Capabilities Provided ({compCaps.length})</Typography>
          <List dense disablePadding sx={{ mb: 1.5 }}>
            {compCaps.map((cc) => (
              <ListItemButton key={cc.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(cc.id, "component_capability")}>
                <ListItemText primary={cc.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#CCFF90" } }} />
              </ListItemButton>
            ))}
          </List>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Enables System Capabilities ({enabledScs.length})</Typography>
          <List dense disablePadding sx={{ mb: 1.5 }}>
            {enabledScs.map((sc) => (
              <ListItemButton key={sc.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(sc.id, "system_capability")}>
                <ListItemText primary={sc.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#80CBC4" } }} />
              </ListItemButton>
            ))}
          </List>
          {companies.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Companies Making This ({companies.length})</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {companies.map((co) => (<Chip key={co.id} label={co.label} size="small" sx={{ fontSize: "0.65rem", cursor: "pointer" }} onClick={() => onNodeClick(co.id, "company")} />))}
              </Box>
            </>
          )}
        </>
      );
    }

    if (nodeKind === "component_instance") {
      const ci = dataset.componentInstances.find((c) => c.id === nodeId);
      if (!ci) return null;
      const ct = dataset.componentTypes.find((t) => t.id === ci.componentTypeId);
      const company = dataset.companies.find((c) => c.id === ci.companyId);
      const compCaps = dataset.componentCapabilities.filter((cc) => ci.componentCapabilityIds.includes(cc.id));
      const enabledScs = dataset.systemCapabilities.filter((sc) => compCaps.some((cc) => cc.enablesSystemCapIds.includes(sc.id)));
      return (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
            {ct && <Chip label={ct.label} size="small" sx={{ bgcolor: "#37474F", color: "#fff", fontSize: "0.65rem" }} />}
          </Box>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>{ci.description}</Typography>
          {company && (
            <><Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.25 }}>Manufacturer</Typography>
            <Chip label={company.label} size="small" sx={{ fontSize: "0.65rem", mb: 1.5, cursor: "pointer" }} onClick={() => onNodeClick(company.id, "company")} /></>
          )}
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Component Capabilities ({compCaps.length})</Typography>
          <List dense disablePadding sx={{ mb: 1.5 }}>
            {compCaps.map((cc) => (
              <ListItemButton key={cc.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(cc.id, "component_capability")}>
                <ListItemText primary={cc.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#CCFF90" } }} />
              </ListItemButton>
            ))}
          </List>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Enables System Capabilities ({enabledScs.length})</Typography>
          <List dense disablePadding>
            {enabledScs.map((sc) => (
              <ListItemButton key={sc.id} sx={{ px: 0, py: 0.25 }} onClick={() => onNodeClick(sc.id, "system_capability")}>
                <ListItemText primary={sc.label} primaryTypographyProps={{ variant: "caption", sx: { color: "#80CBC4" } }} />
              </ListItemButton>
            ))}
          </List>
        </>
      );
    }

    return null;
  }, [selection, dataset, onNodeClick]);

  const title = useMemo(() => {
    if (!selection.nodeId || !selection.nodeKind) return "";
    const { nodeId, nodeKind } = selection;
    if (nodeKind === "capability_cluster") return dataset.clusters.find((c) => c.id === nodeId)?.label ?? "";
    if (nodeKind === "sub_capability") return dataset.clusters.flatMap((c) => c.subCapabilities).find((s) => s.id === nodeId)?.label ?? "";
    if (nodeKind === "use_case") return dataset.clusters.flatMap((c) => c.subCapabilities).flatMap((s) => s.useCases).find((u) => u.id === nodeId)?.label ?? "";
    if (nodeKind === "tender") return dataset.tenders.find((t) => t.id === nodeId)?.label ?? "";
    if (nodeKind === "system_type") return dataset.systemTypes.find((s) => s.id === nodeId)?.label ?? "";
    if (nodeKind === "system_instance") return dataset.systemInstances.find((s) => s.id === nodeId)?.label ?? "";
    if (nodeKind === "system_capability") return dataset.systemCapabilities.find((s) => s.id === nodeId)?.label ?? "";
    if (nodeKind === "component_capability") return dataset.componentCapabilities.find((c) => c.id === nodeId)?.label ?? "";
    if (nodeKind === "component_type") return dataset.componentTypes.find((c) => c.id === nodeId)?.label ?? "";
    if (nodeKind === "component_instance") return dataset.componentInstances.find((c) => c.id === nodeId)?.label ?? "";
    if (nodeKind === "company") return dataset.companies.find((c) => c.id === nodeId)?.label ?? "";
    if (nodeKind === "icp_cluster") return dataset.companies.flatMap((c) => c.icp).find((i) => i.id === nodeId)?.label ?? "";
    return "";
  }, [selection, dataset]);

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={open}
      PaperProps={{
        sx: { width: 320, bgcolor: "background.paper", borderLeft: 1, borderColor: "divider", display: "flex", flexDirection: "column" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", px: 2, pt: 1.5, pb: 1, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Box>
          {selection.nodeKind && (
            <Chip label={KIND_LABELS[selection.nodeKind] ?? selection.nodeKind} size="small" sx={{ bgcolor: KIND_COLORS[selection.nodeKind] ?? "#424242", color: "#fff", fontSize: "0.65rem", height: 18, mb: 0.5 }} />
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{title}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ mt: -0.5, ml: 1, flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflow: "auto", px: 2, py: 1.5 }}>{content}</Box>
    </Drawer>
  );
};

export default CapabilityMapDetailPanel;
